// GraphQL operations for the API-keys management screen (list / create / revoke).
//
// API keys are per-user credentials that authenticate programmatic calls to the
// same AppSync API as their owner, subject to per-key operation scopes and an
// optional project allow-list. These management operations are for signed-in
// Cognito users only; the plaintext key is returned exactly once, at creation.

import { gql } from '@apollo/client';

/** An API key as the management UI renders it (never includes the plaintext). */
export interface ApiKeyView {
  readonly id: string;
  readonly name: string;
  readonly scopes: readonly string[];
  readonly projectIds: readonly string[];
  readonly maskedKey: string;
  readonly createdAt: string;
  readonly expiresAt?: string | null;
  readonly lastUsedAt?: string | null;
  readonly revoked: boolean;
}

/** Shape of the `myApiKeys` query result. */
export interface MyApiKeysData {
  readonly myApiKeys: readonly ApiKeyView[];
}

/** List the signed-in user's API keys (no plaintext/hash ever returned). */
export const MY_API_KEYS = gql`
  query MyApiKeys {
    myApiKeys {
      id
      name
      scopes
      projectIds
      maskedKey
      createdAt
      expiresAt
      lastUsedAt
      revoked
    }
  }
`;

/** Variables for `createApiKey`. */
export interface CreateApiKeyVariables {
  readonly input: {
    readonly name: string;
    readonly scopes: readonly string[];
    readonly projectIds?: readonly string[];
    readonly expiresAt?: string;
  };
}

/** Result of `createApiKey`: the stored key plus the one-time plaintext. */
export interface CreateApiKeyData {
  readonly createApiKey: {
    readonly apiKey: ApiKeyView;
    readonly plaintextKey: string;
  };
}

/**
 * Create an API key. The full `plaintextKey` is returned ONCE and is never
 * retrievable again — the UI must surface it immediately with a copy affordance.
 */
export const CREATE_API_KEY = gql`
  mutation CreateApiKey($input: CreateApiKeyInput!) {
    createApiKey(input: $input) {
      apiKey {
        id
        name
        scopes
        projectIds
        maskedKey
        createdAt
        expiresAt
        lastUsedAt
        revoked
      }
      plaintextKey
    }
  }
`;

/** Variables for `revokeApiKey`. */
export interface RevokeApiKeyVariables {
  readonly input: { readonly keyId: string };
}

/** Result of `revokeApiKey`. */
export interface RevokeApiKeyData {
  readonly revokeApiKey: ApiKeyView;
}

/** Revoke (disable) an API key without deleting it. */
export const REVOKE_API_KEY = gql`
  mutation RevokeApiKey($input: RevokeApiKeyInput!) {
    revokeApiKey(input: $input) {
      id
      revoked
    }
  }
`;
